import { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';
import { useData } from './DataContext';
interface PermissionsContextType {
  isSuperAdmin: () => boolean;
  isAdminForCompany: (companyId?: string) => boolean;
  hasAdminAccess: (companyId?: string) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { customClaims } = useAuth();
  const { targetCompany } = useData();

  const isSuperAdmin = (): boolean => {
    return customClaims?.isSuperAdmin ?? false;
  };

  const isAdminForCompany = (companyId?: string): boolean => {
    const targetId = companyId || targetCompany;
    if (!targetId) return false;
    return customClaims?.adminInCompanies?.[targetId] ?? false;
  };

  const hasAdminAccess = (companyId?: string): boolean => {
    return isSuperAdmin() || isAdminForCompany(companyId);
  };

  const value = {
    isSuperAdmin,
    isAdminForCompany,
    hasAdminAccess,
  };

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
